import { EmptyPage } from "@components/common/empty-page/empty-page";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { CreateTeam } from "@pages/members/teams/create-team";
import {
  TEAM_DISPLAY_NAME,
  TEAMS_DISPLAY_NAME,
} from "@src/constants/team-constants";
import {
  MembersDuoIcon,
  red,
  SearchDuoIcon,
  XMarkCircleFillIcon,
} from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { FetchingStatus } from "@store/store-types";
import { searchSelector } from "@store/ui/ui-selector";
import { getSvgColoredIconCss } from "@styles/common-styles";

interface Props {
  /** The fetching status of the teams */
  status: FetchingStatus;
}

/** Empty page content for teams table */
export function TeamsEmptyPage({ status }: Props): JSX.Element {
  const { debouncedSearchText } = useAppSelector(searchSelector);
  const { canCreateNewTeam } = useHasUserValidRoleCompanyLevel();

  if (debouncedSearchText.length) {
    return (
      <EmptyPage
        title="No results found"
        subtitle={`There are no ${TEAMS_DISPLAY_NAME} that match this search.`}
        icon={SearchDuoIcon}
      />
    );
  }

  if (status === "rejected") {
    return (
      <EmptyPage
        title="Error"
        subtitle={`Failed to fetch the ${TEAMS_DISPLAY_NAME}. Please reload the page and try again.`}
        icon={() => (
          <XMarkCircleFillIcon
            sx={{
              ...getSvgColoredIconCss(red[500]),
              width: "61px",
              height: "61px",
            }}
          />
        )}
      />
    );
  }

  return (
    <EmptyPage
      title={`You don't have any ${TEAMS_DISPLAY_NAME} yet`}
      subtitle={`You haven't added ${TEAMS_DISPLAY_NAME} to your workspace. Select the "new ${TEAM_DISPLAY_NAME}" button to create one.`}
      icon={MembersDuoIcon}
      button={canCreateNewTeam && <CreateTeam variant="primary" />}
    />
  );
}
